import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import MobileDropdown from '@/components/MobileDropdown';
import { Plus, X, TrendingDown, DollarSign, Percent } from 'lucide-react';

const UNITS = [
  { value: 'kg', label: 'kg' }, { value: 'g', label: 'g' }, { value: 'lb', label: 'lb' },
  { value: 'oz', label: 'oz' }, { value: 'L', label: 'L' }, { value: 'ml', label: 'ml' }, { value: 'unidad', label: 'unidad' },
];
const REASONS = [
  { value: 'Limpieza/Pelado', label: 'Limpieza/Pelado' }, { value: 'Deshuesado', label: 'Deshuesado' },
  { value: 'Maduracion', label: 'Maduracion' }, { value: 'Dano en transporte', label: 'Dano en transporte' },
  { value: 'Caducidad', label: 'Caducidad' }, { value: 'Preparacion', label: 'Preparacion' }, { value: 'Otro', label: 'Otro' },
];

export default function Waste() {
  const { axiosAuth, user } = useAuth();
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ingredient_id: '', quantity_received: '', quantity_after_waste: '', unit: 'kg', reason: '', date: '' });
  const currency = user?.currency || 'USD';

  const load = async () => {
    try {
      const [r, s, i] = await Promise.all([axiosAuth().get('/waste'), axiosAuth().get('/waste/summary'), axiosAuth().get('/ingredients')]);
      setRecords(r.data); setSummary(s.data); setIngredients(i.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const ingOptions = ingredients.map(i => ({ value: i.id, label: `${i.name} (${i.unit})` }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ing = ingredients.find(i => i.id === form.ingredient_id);
    try {
      await axiosAuth().post('/waste', {
        ...form, ingredient_name: ing?.name || '',
        quantity_received: parseFloat(form.quantity_received) || 0,
        quantity_after_waste: parseFloat(form.quantity_after_waste) || 0,
      });
      setForm({ ingredient_id: '', quantity_received: '', quantity_after_waste: '', unit: 'kg', reason: '', date: '' });
      setShowForm(false); load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="waste-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Mermas y Desperdicios</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Control de Mermas</h2>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="add-waste-btn" className="flex items-center gap-2 px-4 py-2 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors">
          <Plus size={14} /> Registrar
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="waste-summary">
          <div className="p-6 bg-[#0A0A0A] border border-white/5"><Percent size={18} className="text-amber-400 mb-3" /><p className="text-2xl font-bold text-amber-400">{summary.avg_waste_pct?.toFixed(1)}%</p><p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Merma Promedio</p></div>
          <div className="p-6 bg-[#0A0A0A] border border-white/5"><DollarSign size={18} className="text-red-400 mb-3" /><p className="text-2xl font-bold text-red-400">{currency} {summary.total_cost_lost?.toFixed(2)}</p><p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Costo Perdido</p></div>
          <div className="p-6 bg-[#0A0A0A] border border-white/5"><TrendingDown size={18} className="text-white/40 mb-3" /><p className="text-2xl font-bold">{summary.total_records}</p><p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Registros Totales</p></div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full" data-testid="waste-table">
          <thead><tr className="border-b border-white/10">
            <th className="text-left text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-4">Ingrediente</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-4">Recibido</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-4">Despues Merma</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-4">Merma %</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-4">Costo Perdido</th>
            <th className="text-left text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-4">Razon</th>
          </tr></thead>
          <tbody>
            {records.map((r, i) => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`waste-row-${i}`}>
                <td className="py-3 px-4 text-sm">{r.ingredient_name}</td>
                <td className="py-3 px-4 text-sm text-right">{r.quantity_received} {r.unit}</td>
                <td className="py-3 px-4 text-sm text-right">{r.quantity_after_waste} {r.unit}</td>
                <td className="py-3 px-4 text-sm text-right"><span className={r.waste_percentage > 15 ? 'text-red-400' : 'text-amber-400'}>{r.waste_percentage}%</span></td>
                <td className="py-3 px-4 text-sm text-right text-red-400 font-mono">{currency} {r.cost_lost?.toFixed(2)}</td>
                <td className="py-3 px-4 text-xs text-white/40">{r.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {records.length === 0 && <p className="text-center text-white/30 py-12 text-sm">No hay registros de merma</p>}
      </div>

      {showForm && (
        <div data-testid="waste-form-modal">
          <div className="flex items-center gap-3 mb-6">
            <button type="button" onClick={() => setShowForm(false)} className="text-white/40 hover:text-white text-sm flex items-center gap-1"><X size={14} /> Volver</button>
            <h3 className="font-heading text-xl font-bold">Registrar Merma</h3>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-lg p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Ingrediente *</label>
                <MobileDropdown options={ingOptions} value={form.ingredient_id} onChange={v => setForm(p => ({ ...p, ingredient_id: v }))} testId="waste-ingredient-select" placeholder="Seleccionar ingrediente..." required />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div><label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Recibido</label>
                  <input type="number" step="0.01" value={form.quantity_received} onChange={e => setForm(p => ({ ...p, quantity_received: e.target.value }))} required data-testid="waste-received-input" className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:border-gold focus:outline-none" /></div>
                <div><label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Despues Merma</label>
                  <input type="number" step="0.01" value={form.quantity_after_waste} onChange={e => setForm(p => ({ ...p, quantity_after_waste: e.target.value }))} required data-testid="waste-after-input" className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:border-gold focus:outline-none" /></div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Unidad</label>
                  <MobileDropdown options={UNITS} value={form.unit} onChange={v => setForm(p => ({ ...p, unit: v }))} testId="waste-unit-select" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Razon</label>
                <MobileDropdown options={REASONS} value={form.reason} onChange={v => setForm(p => ({ ...p, reason: v }))} testId="waste-reason-select" placeholder="Seleccionar razon..." />
              </div>
              <div><label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Fecha</label>
                <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} data-testid="waste-date-input" className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:border-gold focus:outline-none" /></div>
              <button type="submit" data-testid="save-waste-btn" className="w-full py-3 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors mt-4">Registrar Merma</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
